import React, { useState, useRef } from "react";

const Values = ({ userData, onComplete, onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const containerRef = useRef(null);

  const minSwipeDistance = 50;

  const pages = [
    {
      title: "Turn everyday moments into magical family adventures",
      subtitle: "Deepen bonds with your kids through the power of play! 10-minute bursts of enriching activities, guided by an AI-powered experience.",
      image: "/images/group1.png",
      custom: "pt-[0px]",
    },
    {
      title: "Your AI listens, reacts, and shapes the fun around you",
      subtitle: "Watch the experience evolve as the AI personalizes it in real-time based on what you say and do!",
      image: "/images/group2.png",
      custom: "pt-[14px] !w-[84%]",
    },
    {
      title: "Like families, no two jams are the same",
      subtitle: "Daily doses of thought-provoking puzzles, mind-boggling conundrums, and creative explorations to nourish your kid's EQ and soft skills.",
      image: "/images/group3.png",
      custom: "pt-[50px]",
    },
    {
      title: "Instant fun, zero prep",
      subtitle: "Unlock a bundle of activities at the click of a button. Perfect for busy families and snack breaks!",
      image: "/images/group4.png",
      custom: "pt-[14px] max-w-[90%]",
    },
  ];

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setTouchEnd(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentStep < pages.length) {
      setCurrentStep(prev => prev + 1);
    }
    if (isRightSwipe && currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const getTransform = () => {
    if (!isDragging) return 'translateX(0)';
    const diff = currentX - startX;
    const maxDiff = window.innerWidth * 0.3;
    const boundedDiff = Math.max(Math.min(diff, maxDiff), -maxDiff);
    return `translateX(${boundedDiff}px)`;
  };

  const handleContinue = () => {
    if (currentStep < pages.length) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete();
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-between bg-[#fafafa] overflow-hidden">
      <div className="w-full max-w-md mx-auto flex flex-col flex-1 px-6 relative">
        <div className="w-full pt-[60px] absolute left-[20px]">
          <button
            onClick={onBack}
            className="bg-white rounded-full p-1 shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-400"
              fill="none"
              viewBox="0 0 26 26"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>

        <div
          className="flex-1 w-full flex items-center justify-center transition-transform duration-300"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          ref={containerRef}
          style={{
            transform: getTransform(),
            touchAction: 'pan-y pinch-zoom'
          }}
        >
          <img
            src={pages[currentStep - 1].image}
            alt={`Step ${currentStep}`}
            className={`w-full h-auto object-contain rounded-lg ${pages[currentStep - 1].custom}`}
            draggable="false"
          />
        </div>

        <div className="space-y-4">
          <h1 className="text-[30px] leading-[36px] font-normal text-center font-young-serif">
            {pages[currentStep - 1].title}
          </h1>
          <p className="text-[16px] leading-[20px] font-normal text-[#1E0A1E] text-center font-space-grotesk">
            {pages[currentStep - 1].subtitle}
          </p>
        </div>

        <div className="flex items-center justify-center space-x-2 mt-8">
          {pages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index + 1)}
              className={`h-6 w-6 flex items-center justify-center rounded-full font-space-grotesk text-[16px] font-normal ${
                currentStep === index + 1
                  ? "bg-[#B08CFF] text-[#1E0A1E] shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)]"
                  : "bg-[#EEEEEE] text-gray-400"
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        <div className="w-full flex justify-center my-8">
          <button
            onClick={handleContinue}
            className="font-space-grotesk bg-black text-[#EEEEEE] text-md font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 shadow-[2px_2px_4px_rgba(0,0,0,0.05),_5px_5px_2px_rgba(0,0,0,0.15)] hover:shadow-2xl"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default Values;